import { Component, HostListener, Input, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AHRService, Milestone } from '../../../../domain/data-access-interfaces';
import { ReviewDateEntry } from '../../../../domain/data-access-interfaces/ahr.service';

interface OverrideRowVm {
    milestone: Milestone;
    periodDate: Date | null;
    override: Date | null;
}

/**
 * Per-case review-date override editor shown on the packet detail page.
 * Overrides win over the period-wide review dates for this case only;
 * clearing a date reverts the case to the period-wide value. The backing
 * endpoints are AHR-admin-only, so the card hides itself when the initial
 * load is rejected.
 */
@Component({
    selector: 'case-review-date-overrides',
    templateUrl: './case-review-date-overrides.component.html'
})
export class CaseReviewDateOverridesComponent implements OnInit {
    @Input() caseId!: number;
    @Input() periodOfReviewId!: number;

    visible = false;
    expanded = false;
    rows: OverrideRowVm[] = [];
    saving = false;

    // Last-saved override value per milestone key (ISO date or null = no override).
    // Edits are compared against this so the editor knows when it is dirty; nothing
    // takes effect server-side until Save is clicked.
    private savedByKey = new Map<string, string | null>();

    constructor(
        private readonly svc: AHRService,
        private readonly sb: MatSnackBar
    ) { }

    ngOnInit(): void {
        Promise.all([
            this.svc.getReviewDates(this.periodOfReviewId),
            this.svc.getCaseReviewDateOverrides(this.caseId)
        ]).then(([period, overrides]) => {
            const periodByKey = new Map<string, Date>();
            period.dates.forEach(d => {
                if (d.date) periodByKey.set(d.milestoneKey, this.parseIsoDate(d.date));
            });
            const overrideByKey = new Map<string, Date>();
            overrides.dates.forEach(d => {
                if (d.date) overrideByKey.set(d.milestoneKey, this.parseIsoDate(d.date));
            });
            this.rows = period.milestones.map(m => ({
                milestone: m,
                periodDate: periodByKey.get(m.key) ?? null,
                override: overrideByKey.get(m.key) ?? null
            }));
            this.captureSaved();
            this.expanded = this.overrideCount > 0;
            this.visible = true;
        }).catch(() => { this.visible = false; });
    }

    get overrideCount(): number {
        return this.rows.filter(r => r.override !== null).length;
    }

    get hasInvalidDate(): boolean {
        return this.rows.some(r => r.override instanceof Date && isNaN(r.override.getTime()));
    }

    /** True when an in-progress edit differs from the last-saved state. */
    get hasUnsavedChanges(): boolean {
        return this.rows.some(r => this.rowOverrideIso(r) !== (this.savedByKey.get(r.milestone.key) ?? null));
    }

    /** Discard in-progress edits, reverting every row to its last-saved value. */
    cancel(): void {
        this.rows.forEach(r => {
            const saved = this.savedByKey.get(r.milestone.key) ?? null;
            r.override = saved ? this.parseIsoDate(saved) : null;
        });
        this.sb.open('Changes discarded.', undefined, { duration: 2500 });
    }

    save(): void {
        (document.activeElement as HTMLElement | null)?.blur?.();
        if (this.hasInvalidDate) {
            this.sb.open('Fix invalid dates before saving.', undefined, { duration: 4000 });
            return;
        }
        const entries: ReviewDateEntry[] = this.rows
            .filter(r => r.override instanceof Date && !isNaN(r.override.getTime()))
            .map(r => ({ milestoneKey: r.milestone.key, date: this.toIsoDate(r.override!) }));
        this.saving = true;
        this.svc.saveCaseReviewDateOverrides(this.caseId, entries).then(result => {
            this.saving = false;
            result.match(
                () => {
                    // Only now do the edits become the saved baseline — the change
                    // had no effect until this point.
                    this.captureSaved();
                    this.sb.open('Case review-date overrides saved', undefined, { duration: 3000 });
                },
                err => this.sb.open(`Save failed: ${err}`, undefined, { duration: 4000 }));
        }).catch(() => {
            // HTTP-level rejection (network/403/session expiry) never reaches the
            // Result path — reset the button and report rather than spinning on 'Saving…'.
            this.saving = false;
            this.sb.open('Save failed: the request could not be completed.', undefined, { duration: 4000 });
        });
    }

    /** Snapshot the current override values as the saved baseline. */
    private captureSaved(): void {
        this.savedByKey = new Map(this.rows.map(r => [r.milestone.key, this.rowOverrideIso(r)]));
    }

    private rowOverrideIso(r: OverrideRowVm): string | null {
        return r.override instanceof Date && !isNaN(r.override.getTime()) ? this.toIsoDate(r.override) : null;
    }

    // Browser-level guard (refresh / close / hard navigation). The SPA in-app
    // guard is the route's CanDeactivate (see unsaved-changes.guard.ts), which
    // calls confirmLeave().
    @HostListener('window:beforeunload', ['$event'])
    onBeforeUnload(e: BeforeUnloadEvent): void {
        if (this.hasUnsavedChanges) {
            e.preventDefault();
            e.returnValue = '';
        }
    }

    /** Returns true if it's safe to leave; otherwise prompts to confirm. */
    confirmLeave(): boolean {
        if (!this.hasUnsavedChanges) return true;
        return confirm('You have unsaved review-date override changes that will be lost. Leave anyway?');
    }

    private toIsoDate(d: Date): string {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }

    private parseIsoDate(input: string): Date {
        const s = input.indexOf('T') > 0 ? input.substring(0, input.indexOf('T')) : input;
        const [y, m, d] = s.split('-').map(n => parseInt(n, 10));
        return new Date(y, m - 1, d);
    }
}
